import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { EvaluationPathType } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { EvaluationDifficulty } from "../../../../api/graphql";
import { ShowBoolean } from "../../../common/controls";
import { EvaluationAmbiguityType } from "../../../../api/graphql";
import { FormControl } from "@mui/material";

export type EvaluationQueryViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  queryValue?: { displayId: string; id: string } | null;
  query: string;
  pathTypeOptions: { id: string; displayId: string }[];
  pathType: EvaluationPathType;
  difficultyOptions: { id: string; displayId: string }[];
  difficulty: EvaluationDifficulty;
  expectedToolValue?: { displayId: string; id: string } | null;
  expectedTool: string;
  expectedArgsValue?: { displayId: string; id: string } | null;
  expectedArgs: string;
  acceptableToolsValue?: { displayId: string; id: string } | null;
  acceptableTools: string;
  expectedResponsePatternValue?: { displayId: string; id: string } | null;
  expectedResponsePattern: string;
  generatedFromValue?: { displayId: string; id: string } | null;
  generatedFrom: string;
  isAmbiguousValue?: { displayId: string; id: string } | null;
  isAmbiguous: boolean;
  ambiguityTypeOptions: { id: string; displayId: string }[];
  ambiguityType: EvaluationAmbiguityType | "";
  disambiguationResponseValue?: { displayId: string; id: string } | null;
  disambiguationResponse: string;
  notesValue?: { displayId: string; id: string } | null;
  notes: string;
};

export const EvaluationQueryViewForm: React.FC<EvaluationQueryViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      query = "",
      pathTypeOptions = [],
      pathType = "" as EvaluationPathType,
      difficultyOptions = [],
      difficulty = "" as EvaluationDifficulty,
      expectedTool = "",
      expectedArgs = "{}",
      acceptableTools = "{}",
      expectedResponsePattern = "",
      generatedFrom = "{}",
      isAmbiguous = false,
      ambiguityTypeOptions = [],
      ambiguityType = "" as EvaluationAmbiguityType | "",
      disambiguationResponse = "",
      notes = "",
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupEvaluationQueryLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={query}
                  label={langStore.EvaluationQueryQueryLabel}
                />
                <ShowString
                  value={
                    pathType
                      ? pathTypeOptions.find(
                          (o: { id: string }) => o.id === pathType
                        )?.displayId || pathType
                      : ""
                  }
                  label={langStore.EvaluationQueryPathTypeLabel}
                />
                <ShowString
                  value={
                    difficulty
                      ? difficultyOptions.find(
                          (o: { id: string }) => o.id === difficulty
                        )?.displayId || difficulty
                      : ""
                  }
                  label={langStore.EvaluationQueryDifficultyLabel}
                />
                <ShowMultiLine
                  value={expectedTool}
                  label={langStore.EvaluationQueryExpectedToolLabel}
                />
                <ShowMultiLine
                  value={expectedArgs}
                  label={langStore.EvaluationQueryExpectedArgsLabel}
                />
                <ShowMultiLine
                  value={acceptableTools}
                  label={langStore.EvaluationQueryAcceptableToolsLabel}
                />
                <ShowMultiLine
                  value={expectedResponsePattern}
                  label={langStore.EvaluationQueryExpectedResponsePatternLabel}
                />
                <ShowMultiLine
                  value={generatedFrom}
                  label={langStore.EvaluationQueryGeneratedFromLabel}
                />
                <ShowBoolean
                  value={isAmbiguous}
                  label={langStore.EvaluationQueryIsAmbiguousLabel}
                />
                <ShowString
                  value={
                    ambiguityType
                      ? ambiguityTypeOptions.find(
                          (o: { id: string }) => o.id === ambiguityType
                        )?.displayId || ambiguityType
                      : ""
                  }
                  label={langStore.EvaluationQueryAmbiguityTypeLabel}
                />
                <ShowMultiLine
                  value={disambiguationResponse}
                  label={langStore.EvaluationQueryDisambiguationResponseLabel}
                />
                <ShowMultiLine
                  value={notes}
                  label={langStore.EvaluationQueryNotesLabel}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
